/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/naming-convention */
import { Observable, from, of } from 'rxjs';
import { v4 as uuidV4 } from 'uuid';
import {
  map,
  take,
  switchMap,
  catchError,
  shareReplay,
  timeout
} from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { ExperimentClient } from '@amplitude/experiment-js-client';
import { AnalyticsBrowser } from '@segment/analytics-next';
import { ConfigurationService, IAmplitudeAppConfig } from '@sondermind/configuration';
import { CurrentUser } from '@sondermind/data-access/client-user';
import { Logger } from '@sondermind/datadog-logging';
import { AmplitudeFeatureFlags } from '../models/amplitude-feature-flags.enum';

// Amplitude FF service setup according to SDK guide:
// https://www.docs.developers.amplitude.com/experiment/sdks/javascript-sdk/#configuration

// Default timeout for fetch requests
const DEFAULT_TIMEOUT = 3000;
const LOCAL_DEV_SERVER_URL = 'https://api.lab.amplitude.com';

interface IEvaluationOptions {
  evaluateUser?: IExperimentUser;
  timeout?: number;
  forceRefresh?: boolean;
}

export interface IExperimentUser {
  user_id: string;
  device_id?: string;
  user_properties?: Record<string, unknown>;
}

export type FeatureFlagVariant<TKey extends string = string, TPayload = unknown> = {
  key: TKey;
  payload: TPayload;
};

export type EvaluationContext = {
  accountId: string;
};

@Injectable({
  providedIn: 'root'
})
export class AmplitudeFeatureFlagService {
  private experimentClient?: ExperimentClient;
  private analytics?: AnalyticsBrowser;
  private initialized = false;
  private deviceId: string;

  constructor(
    private configurationService: ConfigurationService<IAmplitudeAppConfig>,
    private currentUser: CurrentUser,
    private logger: Logger,
  ) { }

  initialize$(): Observable<boolean> {
    if (this.initialized) {
      return of(true);
    }

    const config = this.configurationService.env.app;
    if (!config?.amplitudeDeploymentKey) {
      return of(false);
    }

    const isLocalDevelopment = window.location.hostname === 'localhost';

    // uncomment debug property for amplitude console logging
    this.experimentClient = new ExperimentClient(config.amplitudeDeploymentKey, {
      // debug: this.configurationService.env.sentryDebug,
      serverUrl: isLocalDevelopment ? LOCAL_DEV_SERVER_URL : undefined
    });

    this.initialized = true;

    return this.anonymousId$().pipe(
      switchMap((anonymousId) => {
        this.deviceId = anonymousId;
        return of(true);
      }),
      catchError((error) => of(false))
    );
  }

  evaluate$<TKey extends string = string, TPayload = unknown>(
    flagKey: AmplitudeFeatureFlags,
    defaultValue: TPayload,
    options?: IEvaluationOptions
  ): Observable<FeatureFlagVariant<TKey, TPayload> | null> {
    return this.initialize$().pipe(
      switchMap((initialized) => {
        if (!initialized || !this.experimentClient) {
          return of(null);
        }

        return (this.currentUser.user$ ?? of({ platformAccountUuid: 'anonymous' })).pipe(
          take(1),
          switchMap((currentUser) => {
            const amplitudeUser = {
              device_id: this.deviceId
            };

            if (!!currentUser?.platformAccountUuid && currentUser?.platformAccountUuid !== 'anonymous') {
              amplitudeUser['user_id'] = currentUser?.platformAccountUuid;
            }

            return from(this.experimentClient.fetch(amplitudeUser)).pipe(
              switchMap(() => this.evaluateVariant$<TKey, TPayload>(flagKey)),
              timeout(options?.timeout ?? DEFAULT_TIMEOUT)
            );
          })
        );
      }),
      catchError((error) => of(null)),
      shareReplay(1)
    );
  }

  isEnabled$(flagKey: AmplitudeFeatureFlags, defaultValue = false): Observable<boolean> {
    return this.evaluate$<string, boolean>(flagKey, defaultValue).pipe(
      map((variant) => ['on', 'true', 'enabled'].includes(variant?.key ?? ''))
    );
  }

  private evaluateVariant$<TKey extends string = string, TPayload = unknown>(flagKey: string): Observable<FeatureFlagVariant<TKey, TPayload> | null> {
    if (!this.experimentClient) {
      return of(null);
    }

    try {
      const variant = this.experimentClient.variant(flagKey);
      if (!variant?.key) {
        return of(null);
      }

      return of({
        key: variant.key as TKey,
        payload: variant.payload as TPayload
      });
    } catch (error) {
      return of(null);
    }
  }

  getVariant$<TKey extends string = string, TPayload = unknown>(context: EvaluationContext, flagKey: string): Observable<FeatureFlagVariant<TKey, TPayload> | null> {
    return this.getVariantsForUser$(context.accountId).pipe(
      map((variants) => {
        const variant = variants[flagKey];
        // If variant is undefined, it means the experiment is not active.
        if (!variant?.key || variant.key === 'off' || variant.key === 'disabled' || variant.key === 'false') return null;

        return {
          key: variant.key as TKey,
          payload: variant.payload as TPayload
        };
      }),
      catchError((error) => of(null))
    );
  }

  private getVariantsForUser$(userId: string): Observable<Record<string, FeatureFlagVariant>> {
    return this.initialize$().pipe(
      switchMap((initialized) => {
        if (!initialized || !this.experimentClient) return of({});

        return from(this.experimentClient.fetch({ user_id: userId })).pipe(
          map((variants) => Object.entries(variants).reduce((acc, [key, variant]) => {
            // Skip variants with undefined keys
            if (!variant?.key) return acc;

            acc[key] = {
              key: variant.key,
              payload: variant.payload
            };
            return acc;
          }, {} as Record<string, FeatureFlagVariant>)),
          catchError((error) => of({}))
        );
      })
    );
  }

  anonymousId$(): Observable<string | undefined> {
    if (!this.configurationService.env.app?.segmentWriteKey) {
      this.logger.warn(
        'No segment write key was provided for the amplitude feature flag service. ' +
        'Continuing by using a generated device ID'
      );
    } else {
      this.analytics = AnalyticsBrowser.load(
        { writeKey: this.configurationService.env.app?.segmentWriteKey }
      );
    }

    if (!this.analytics) {
      this.deviceId = uuidV4();
      return of(this.deviceId);
    }

    return from(this.analytics.user()).pipe(
      switchMap((analyticsUser) => {
        const anonymousId = analyticsUser?.anonymousId();
        this.deviceId = anonymousId;
        return of(anonymousId);
      }),
      catchError((error) => of(undefined))
    );
  }
}
